import React from 'react';

const LeadSelector = ({
  pool,
  poolType,
  selectedLeads,
  onSelectLead,
  onSelectQuantity,
  onClearSelection
}) => {
  const [customQuantity, setCustomQuantity] = React.useState('');
  const [showLeadList, setShowLeadList] = React.useState(true);

  const handleSelectQuantity = (quantity) => {
    if (quantity === 'all' || quantity >= pool.length) {
      onSelectQuantity(pool.map(lead => lead.id));
    } else {
      onSelectQuantity(pool.slice(0, quantity).map(lead => lead.id));
    }
  };

  const handleCustomSelect = () => {
    const qty = parseInt(customQuantity);
    if (qty > 0) {
      handleSelectQuantity(qty);
      setCustomQuantity(''); // Clear input after selection
    }
  };

  return (
    <div className="bg-white rounded-lg p-3 md:p-4 lg:p-5 shadow-sm">
      <div className="mb-4 pb-4 border-b-2 border-gray-100">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 gap-2.5">
          <h3 className="m-0 text-lg text-gray-800">
            {poolType.toUpperCase()} Pool ({pool.length} leads)
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 font-medium">
              {selectedLeads.length} selected
              {selectedLeads.length > 0 && pool.length > 0 &&
                ` (${((selectedLeads.length / pool.length) * 100).toFixed(0)}%)`
              }
            </span>
          </div>
        </div>

        {/* Quick Selection Buttons */}
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-gray-500 font-medium self-center mr-1">Quick Select:</span>
          {[10, 50, 100, 200].map(qty => (
            <button
              key={qty}
              className="px-3 md:px-3 py-2.5 md:py-1.5 min-h-[44px] md:min-h-0 bg-white text-gray-700 border border-gray-300 rounded-md text-xs font-medium cursor-pointer transition-all hover:bg-tenq-gold/10 hover:border-tenq-gold hover:text-tenq-gold active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handleSelectQuantity(qty)}
              disabled={pool.length === 0}
              title={qty > pool.length ? `Select all ${pool.length} leads` : `Select first ${qty} leads`}
            >
              {qty > pool.length ? `All (${pool.length})` : qty}
            </button>
          ))}

          {/* Custom Quantity Input */}
          <div className="flex items-center gap-1">
            <input
              type="number"
              min="1"
              max={pool.length}
              value={customQuantity}
              onChange={(e) => setCustomQuantity(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCustomSelect();
                }
              }}
              placeholder="Custom"
              className="w-20 md:w-16 px-2 py-2.5 md:py-1.5 min-h-[44px] md:min-h-0 border border-gray-300 rounded-md text-xs font-medium text-center text-gray-700 focus:outline-none focus:border-tenq-gold focus:ring-1 focus:ring-tenq-gold disabled:opacity-50 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              disabled={pool.length === 0}
              inputMode="numeric"
            />
            <button
              className="px-2 md:px-2 py-2.5 md:py-1.5 min-h-[44px] md:min-h-0 bg-white text-gray-700 border border-gray-300 rounded-md text-xs font-medium cursor-pointer transition-all hover:bg-tenq-gold/10 hover:border-tenq-gold hover:text-tenq-gold active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleCustomSelect}
              disabled={pool.length === 0 || !customQuantity || parseInt(customQuantity) <= 0}
              title="Select custom quantity"
            >
              Go
            </button>
          </div>

          <button
            className="px-3 md:px-3 py-2.5 md:py-1.5 min-h-[44px] md:min-h-0 bg-tenq-gold text-white border border-tenq-gold rounded-md text-xs font-semibold cursor-pointer transition-all hover:bg-tenq-gold-dark active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => handleSelectQuantity('all')}
            disabled={pool.length === 0}
          >
            Select All
          </button>
          <button
            className="px-3 md:px-3 py-2.5 md:py-1.5 min-h-[44px] md:min-h-0 bg-gray-100 text-gray-700 border border-gray-200 rounded-md text-xs font-medium cursor-pointer transition-all hover:bg-gray-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onClearSelection}
            disabled={selectedLeads.length === 0}
          >
            Clear
          </button>
        </div>
      </div>

      {pool.length === 0 ? (
        <div className="text-center py-10 px-5 text-gray-500">
          <p>No {poolType} leads pending distribution</p>
        </div>
      ) : (
        <>
          {/* Toggle Button for Lead List */}
          <button
            onClick={() => setShowLeadList(!showLeadList)}
            className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md text-sm font-medium text-gray-700 transition-all flex items-center justify-center gap-2"
          >
            <span>{showLeadList ? '▼' : '▶'}</span>
            <span>{showLeadList ? 'Hide' : 'Show'} Lead Details ({pool.length} leads)</span>
          </button>

          {/* Collapsible Lead List */}
          {showLeadList && (
            <div className="flex flex-col gap-2 max-h-[500px] overflow-y-auto mt-4">
              {pool.map(assignment => (
                <div
                  key={assignment.id}
                  className={`flex items-center gap-3 p-4 md:p-3 border-2 rounded-md cursor-pointer transition-all min-h-[60px] ${
                    selectedLeads.includes(assignment.id)
                      ? 'bg-tenq-gold/5 border-tenq-gold'
                      : 'bg-gray-50 border-transparent hover:bg-gray-100 hover:border-gray-200'
                  }`}
                  onClick={() => onSelectLead(assignment.id)}
                >
                  <input
                    type="checkbox"
                    checked={selectedLeads.includes(assignment.id)}
                    onChange={() => {}}
                    className="w-5 h-5 md:w-[18px] md:h-[18px] cursor-pointer flex-shrink-0"
                  />
                  <div className="flex-1 flex flex-col gap-1">
                    <div className="font-semibold text-gray-800 text-base">
                      {assignment.lead?.name || 'Unknown'}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 flex-wrap">
                      <span>{assignment.lead?.phoneNumber}</span>
                      <span className="text-gray-300">•</span>
                      <span className="capitalize">{assignment.lead?.platform || 'N/A'}</span>
                      {assignment.lead?.propertyType && (
                        <>
                          <span className="text-gray-300">•</span>
                          <span className="uppercase">{assignment.lead.propertyType}</span>
                        </>
                      )}
                      {assignment.lead?.projectType && (
                        <>
                          <span className="text-gray-300">•</span>
                          <span className="capitalize">{assignment.lead.projectType.replace(/_/g, ' ')}</span>
                        </>
                      )}
                      {assignment.isRecycled && (
                        <>
                          <span className="text-gray-300">•</span>
                          <span className="bg-orange-500 text-white px-1.5 py-0.5 rounded text-[11px] font-semibold">
                            #{assignment.assignmentNumber}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LeadSelector;
